import * as logClient from "@classdojo/log-client";
import type { UppyFile } from "@uppy/core";
import { callApi } from "@web-monorepo/fetchers";
import type { UploadedFile } from "../types";

type UploadInfo<T extends File | UppyFile> = T extends File
  ? UploadedFile
  : Omit<UploadedFile, "file"> & { file: UppyFile };

const getFileType = (file: File | UppyFile) => ("type" in file ? file.type : file.meta.type) ?? "";

const fileToBucketName = (file: File | UppyFile) => {
  const fileType = getFileType(file);
  if (fileType.startsWith("image")) {
    return "photo";
  }
  if (fileType.startsWith("video")) {
    return "video";
  }

  return "file";
};

export const metricLogger = (transport: string) => ({
  success: (metricName: "s3_put" | "transaction") =>
    logClient.sendMetrics([
      {
        metricName: `file_upload.${metricName}`,
        type: "increment",
        value: 1,
        tags: { transport, result: "success" },
      },
    ]),
  failure: (metricName: "s3_put" | "transaction", times: number = 1) => {
    if (times > 0) {
      logClient.sendMetrics([
        {
          metricName: `file_upload.${metricName}`,
          type: "increment",
          value: times,
          tags: { transport, result: "failure" },
        },
      ]);
    }
  },
});

export type UploadSource = "messaging" | "storypost" | "portfolio" | "profile" | "activity";

export type ContentDisposition = "inline" | "attachment";

// S3 presigned POST requires the signed policy fields first and the file appended last; getting that
// order wrong silently breaks the upload. Centralize it so every call site can't get it wrong.
export const buildPresignedPostForm = (fields: Record<string, string>, file: Blob, filename?: string): FormData => {
  const form = new FormData();
  for (const [name, value] of Object.entries(fields)) {
    form.append(name, value);
  }
  if (filename === undefined) {
    form.append("file", file);
  } else {
    form.append("file", file, filename);
  }
  return form;
};

export type GetUploadURLOptions = {
  getBucket?: (file: File | UppyFile) => string;
  uploadSource?: UploadSource;
  contentDisposition?: ContentDisposition;
};

export const getUploadURL = async <T extends File | UppyFile>(
  file: T,
  options?: GetUploadURLOptions,
): Promise<UploadInfo<T>> => {
  const { getBucket = fileToBucketName, uploadSource, contentDisposition } = options ?? {};
  const bucketName = getBucket(file);
  // File has `.type`; UppyFile carries the MIME at `.meta.type`. getFileType normalizes both.
  const contentType = getFileType(file);

  // The size cap, Content-Disposition, and video transcode flag are all decided server-side and
  // baked into the signed presigned-POST policy — the client just declares intent.
  const body: Record<string, unknown> = {
    filename: file.name,
    type: bucketName,
    ...(uploadSource && { uploadSource }),
    ...(contentType && { contentType }),
    ...(contentDisposition && { contentDisposition }),
  };

  const response = await callApi({
    path: "/api/uploadFileURL/v2",
    method: "POST",
    body,
    headers: {
      accept: "application/json",
    },
  });

  const data = response.body as
    | {
        path?: string;
        urlSmall?: string;
        cdnUrl?: string;
        upload?: { url: string; fields: Record<string, string> };
      }
    | undefined;

  if (!data) {
    throw new Error(`BAD RESPONSE - no data: ${JSON.stringify(data)}`);
  }
  if (!data.path) {
    throw new Error(`BAD RESPONSE - no path: ${JSON.stringify(data)}`);
  }
  if (!data.upload?.url) {
    throw new Error(`BAD RESPONSE - no upload url: ${JSON.stringify(data)}`);
  }

  return {
    file,
    url: data.upload.url,
    fields: data.upload.fields,
    key: data.path,
    urlSmall: data.urlSmall ?? "",
    cdnUrl: data.cdnUrl ?? "",
  } as UploadInfo<T>;
};
